import type { IconName } from '@/ui/icons';
import type { ReactNode } from 'react';
import { createStore } from './store';

// Short confirmations at the bottom of the screen. At most three show at once; the oldest makes way. A toast with
// Undo or another action waits while the pointer rests on it or focus is inside it.

export interface ToastOptions {
  icon?: IconName;
  /** Shows an Undo button and keeps the toast up a little longer. */
  undo?: () => void;
  /** A second action, such as "Open". */
  action?: { label: string; run: () => void };
  /** Marks the message as a problem (announced assertively and shown with a warning icon). */
  error?: boolean;
}

export interface ToastItem extends ToastOptions {
  id: number;
  message: ReactNode;
  leaving: boolean;
}

export const toastStore = createStore<{ items: ToastItem[]; raised: boolean }>({ items: [], raised: false });

const PLAIN_MS = 4200;
const ACTION_MS = 6500;
/** Time a held toast keeps once the pointer or focus leaves it. */
const AFTER_HOLD_MS = 3000;

export type ToastHold = 'hover' | 'focus';

interface Clock {
  timer: ReturnType<typeof setTimeout> | null;
  /** When the toast goes, while it is counting down. */
  due: number;
  /** Time left when it was held. */
  left: number;
  holds: Set<ToastHold>;
}

let nextId = 1;
const clocks = new Map<number, Clock>();

function stopClock(id: number): void {
  const c = clocks.get(id);
  if (c?.timer) clearTimeout(c.timer);
  clocks.delete(id);
}

function run(id: number, c: Clock, ms: number): void {
  c.due = Date.now() + ms;
  c.timer = setTimeout(() => dismissToast(id), ms);
}

export function dismissToast(id: number): void {
  stopClock(id);
  toastStore.set((s) => ({ items: s.items.map((x) => (x.id === id ? { ...x, leaving: true } : x)) }));
  setTimeout(() => toastStore.set((s) => ({ items: s.items.filter((x) => x.id !== id) })), 200);
}

/** Keeps a toast up while the pointer is on it or focus is inside it. */
export function holdToast(id: number, why: ToastHold): void {
  const c = clocks.get(id);
  if (!c) return;
  if (c.holds.size === 0 && c.timer) {
    clearTimeout(c.timer);
    c.timer = null;
    c.left = Math.max(0, c.due - Date.now());
  }
  c.holds.add(why);
}

/** Lets a held toast count down again once nothing holds it, with at least a few seconds left. */
export function releaseToast(id: number, why: ToastHold): void {
  const c = clocks.get(id);
  if (!c || !c.holds.delete(why) || c.holds.size > 0) return;
  run(id, c, Math.max(c.left, AFTER_HOLD_MS));
}

export function toast(message: ReactNode, opts: ToastOptions = {}): number {
  const id = nextId++;
  const item: ToastItem = { id, message, leaving: false, ...opts, icon: opts.icon ?? (opts.error ? 'warn' : 'check-circle') };
  toastStore.set((s) => {
    const items = [...s.items, item];
    while (items.filter((x) => !x.leaving).length > 3) {
      const oldest = items.findIndex((x) => !x.leaving);
      stopClock(items[oldest]!.id);
      items.splice(oldest, 1);
    }
    return { items };
  });
  const clock: Clock = { timer: null, due: 0, left: 0, holds: new Set() };
  clocks.set(id, clock);
  run(id, clock, opts.undo || opts.action ? ACTION_MS : PLAIN_MS);
  return id;
}

/** Shows a failed request's message as a toast. */
export function toastError(err: unknown, fallback = 'That did not work. Try again.'): void {
  const message = err instanceof Error && err.message ? err.message : fallback;
  toast(message, { error: true });
}

/** Lifts the toasts above the bulk-actions bar while it shows. */
export function setToastsRaised(raised: boolean): void {
  toastStore.set({ raised });
}
